import React, { useState } from "react";

import { mdiArchiveArrowDownOutline, mdiChartTimeline } from "@mdi/js";
import Icon from "@mdi/react";
import classNames from "classnames";
import moment from "moment";
import { useTranslation } from "react-i18next";

import CalendarDayDetailsSidePanel from "./CalendarDayDetailsSidePanel";
import prettyDurations, { DURATION_UNITS } from "../../../utils/durations";

import type { JSX } from "react";
import type { DateCellWrapperProps } from "react-big-calendar";

import type { DayCalendarFilling } from "../../../api/FastAPI.types";

interface IDateCellWrapper extends DateCellWrapperProps {
  onClick: (date: Date) => void;
  dayFilling?: DayCalendarFilling;
}

function DateCellWrapper({
  children,
  value,
  onClick,
  dayFilling,
}: IDateCellWrapper): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const dayDuration = dayFilling?.scheduled_time ?? 0;
  const isTemplateUsed = Boolean(dayFilling?.template_used);
  const { className } = children.props;
  const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false);

  const dayDurationFormatted = prettyDurations(dayDuration, {
    valueUnit: DURATION_UNITS.SECONDS,
    format: {
      noDays: true,
    },
  });

  const tooltipContent = t("playlistDuration", {
    duration: dayDuration ? dayDurationFormatted?.humanised : t("noDuration"),
  });

  const isSameMonth = moment().isSame(value, "month");
  const isPast = moment(value).isBefore(moment(), "day");
  const isToday = moment(value).isSame(moment(), "day");

  const getBackgroundColor = (): string => {
    if (isPast) {
      return "bg-surface-muted!";
    }
    if (dayDuration === 0) {
      return "bg-card!";
    }
    if (dayDurationFormatted?.number.hours < 24) {
      return "bg-[#EFD340]! text-black! **:text-black!";
    }
    return "bg-status-active!";
  };

  const containerClasses = classNames(
    className,
    "cursor-pointer flex flex-col group text-foreground relative",
    getBackgroundColor(),
  );

  const dayCellClassNames = classNames(
    "p-[0.15625rem] flex justify-center items-center size-[1.5625rem] font-semibold",
    isToday && "bg-primary text-primary-foreground",
  );

  const day = moment(value).format("DD");

  const handleSidePanelClose = (): void => {
    setIsSidePanelOpen(false);
  };

  const handleSidePanelOpen = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setIsSidePanelOpen(true);
  };

  return (
    <React.Fragment>
      <div
        className={containerClasses}
        data-tooltip-content={tooltipContent}
        data-tooltip-id={isSameMonth ? "calendar-date-cell" : undefined}
        onClick={(): void => onClick(value)}
      >
        <div className="flex items-center justify-between">
          <div className={dayCellClassNames}>{day}</div>
        </div>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-[0.15625rem] text-center text-xs">
          {dayDurationFormatted?.humanised}
        </div>
        {isTemplateUsed ? (
          <div className="text-muted-foreground mt-auto flex items-center justify-start p-[0.15625rem]">
            <Icon path={mdiChartTimeline} size={0.78125} />
          </div>
        ) : null}
        <div
          className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 hidden group-hover:block!"
          onClick={handleSidePanelOpen}
        >
          <Icon
            className="mr-[0.15625rem]"
            path={mdiArchiveArrowDownOutline}
            rotate={90}
            size={0.78125}
          />
        </div>
      </div>
      <CalendarDayDetailsSidePanel
        currentDay={value}
        isOpen={isSidePanelOpen}
        onClose={handleSidePanelClose}
        playlistDuration={dayDurationFormatted?.humanised || t("noDuration")}
      />
    </React.Fragment>
  );
}

export default DateCellWrapper;
